import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { getAllAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { Clear, Send } from '@mui/icons-material';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { AutogiroMandateResponse } from '../models/autogiroMandateResponse';
import { AutogiroSearchFilter } from '../models/autogiroSearchFilter';
import { AutogiroMandateSendStatus } from './autogiroMandateSendStatus';
import { AutogiroMandateStatus } from './autogiroMandateStatus';

type AutogiroFileDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: AutogiroSearchFilter;
  searchFilterCallback: (filter: AutogiroSearchFilter) => void;
  checkboxSelection: boolean;
};

export const AutogiroFileDataGrid = (props: AutogiroFileDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback, checkboxSelection } = props;
  const { t } = useTranslation(['invoice']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();
  const { setCreateFileMode, setSelectedAutogiroMandates } = useInvoiceContext();

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [allRows, setAllRows] = useState<AutogiroMandateResponse[]>([]);
  const [rows, setRows] = useState<AutogiroMandateResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const uniqueById = (items: Array<AutogiroMandateResponse>) => {
    const set = new Set();
    return items.filter((item) => {
      const isDuplicate = set.has(item.autogiroMandate_id);
      set.add(item.autogiroMandate_id);
      return !isDuplicate;
    });
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllAutogiroMandate(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.autogiroMandate_id };
            }),
          );
          setAllRows((prev) => {
            return uniqueById([...prev, ...result.data.results]);
          });
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const handleCreateFile = (mandates: AutogiroMandateResponse[]) => {
    setSelectedAutogiroMandates(mandates);
    setCreateFileMode(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('invoice:autogiro.tableHeader.firstName'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:autogiro.tableHeader.lastName'),
      sortable: true,
      flex: 1.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('invoice:autogiro.tableHeader.personalIdentityNumber'),
      sortable: true,
      type: 'number',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.personalIdentityNumber}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:autogiro.tableHeader.createdAt'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)}</>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:autogiro.tableHeader.status'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <AutogiroMandateStatus item={params.row} />;
      },
    },
    {
      field: 'sent',
      headerName: t('invoice:autogiro.tableHeader.sent'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <AutogiroMandateSendStatus item={params.row} />;
      },
    },
    {
      field: 'autogiroMandate_id',
      headerName: t('fundraiser:tableHeader.dateCreated'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.personalIdentityNumber}</>;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
        ref={scrollRef}
      >
        <DataGrid<AutogiroMandateResponse>
          columns={columns}
          rows={rows}
          autoHeight
          dataModel={data}
          columnVisibilityModel={{
            autogiroMandate_id: false,
          }}
          checkboxSelection={checkboxSelection}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={sortModel}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          onSortModelChange={(newSortModel) => {
            ConsoleHelper.log('newSortModel', newSortModel);
            const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
            if (newSortModel.length) {
              setSortModel(newSortModel);
            } else {
              setSortModel([{ field: sortModel[0].field, sort }]);
            }
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
              sortDir: newSortModel.length ? newSortModel[0].sort : sort,
            });
          }}
          selectionModel={selectionModel}
        />
      </Box>
      {checkboxSelection && selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button
              variant='contained'
              disableElevation
              endIcon={<Send fontSize='small' />}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const mandatesFromSelection = allRows.filter((row) => selectedIDs.has(row.autogiroMandate_id || 0));
                if (mandatesFromSelection) handleCreateFile(mandatesFromSelection);
              }}
              sx={{ mr: 1 }}
            >
              {t('invoice:autogiro.tabCreateFile')}
            </Button>
          </Box>
        </FixedFooter>
      )}
    </>
  );
};
